import {
  ChangeEvent,
  FC,
  createRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import * as Styled from "./styled";
import ReactDOM from "react-dom";
import { Button, Checkbox, RadioButton } from "elements";

// @ts-ignore
import reloadIcon from "assets/images/reload.svg";

// @ts-ignore
import closeIcon from "assets/images/journeys/close.svg";

// @ts-ignore
import assistantBeta from "assets/images/assistant-beta.svg";

// @ts-ignore
import assistantLogo from "assets/images/assistant-logo.svg";

// @ts-ignore
import loaderIcon from "assets/images/assistant-loader.gif";

import { DataType, Item } from "./data";
import { websocketConnection } from "utils/websocket";

export interface AssistantProps {
  active: boolean;
  assistant?: {
    type: DataType;
    color?: string;
    title: string;
    order: string;
    area: string;
    category: string;
    goal?: string;
    actions?: string[];
    domain?: string;
    prompt?: string;
    onSelect?: (items: Item | Item[]) => void;
  };
  onSelect?: (item: Item | null) => void;
}

const increaseBy = (num: number) => {
  let counter = num;

  return () => {
    const result = counter;
    counter += 50;
    return result;
  };
};

function randomID(): string {
  return `id_` + Math.floor(10000 + Math.random() * 90000);
}

// Automatic retry: reconnect and resend a pending request a limited number of
// times so a flaky connection recovers on its own without turning into an
// endless connect/disconnect loop.
const RETRY_DELAY = 5000;
const MAX_AUTO_RETRIES = 5;

interface ILoader {
  title: string;
  loading: boolean;
  error?: string;
}

const Loader: FC<ILoader> = ({ title, loading, error }) => {
  const animateDelayCounter = increaseBy(100);

  return (
    <Styled.Loader>
      <Styled.LoaderImg
        src={assistantLogo}
        width={69}
        height={69}
        $animate={loading}
        $animateDelay={animateDelayCounter()}
        alt=""
      />

      <Styled.LoaderText
        $animate={loading}
        $animateDelay={animateDelayCounter()}
      >
        Suggesting {title}...
      </Styled.LoaderText>

      <Styled.LoaderImg
        src={loaderIcon}
        width={52}
        height={53}
        $animate={loading}
        $animateDelay={animateDelayCounter()}
        alt="Loading"
      />

      {error && (
        <Styled.LoaderError
          $animate={!!error}
          $animateDelay={animateDelayCounter()}
        >
          {error}
        </Styled.LoaderError>
      )}
    </Styled.Loader>
  );
};

const Assistant: FC<AssistantProps> = ({
  active,
  assistant: data,
  onSelect,
}) => {
  const [list, setList] = useState<Item[]>([]);

  const [selected, setSelected] = useState<string | string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [shouldRender, setShouldRender] = useState(active);
  const [animationActive, setAnimationActive] = useState(false);

  const $scrollable = createRef<HTMLDivElement>();
  const $wrapperRef = useRef<HTMLDivElement>(null);

  // Tracks whether a suggestion request is awaiting a response. It is used to
  // know when to surface a connection error and to automatically resend the
  // request once the connection is re-established.
  const isPendingRef = useRef(false);

  // Automatic retry bookkeeping (see MAX_AUTO_RETRIES above).
  const retryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);

  const clearRetry = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const scheduleRetry = () => {
    clearRetry();

    // Don't retry forever; after the limit keep the error message on screen.
    if (retryCountRef.current >= MAX_AUTO_RETRIES) {
      return;
    }

    retryCountRef.current += 1;

    retryTimerRef.current = setTimeout(handleRefresh, RETRY_DELAY);
  };

  useEffect(() => {
    if (!active) return;

    isPendingRef.current = true;
    retryCountRef.current = 0;
    setLoading(true);
    setError(false);
    setErrorMessage("");

    websocketConnection.connect(handleOpen, handleError, handleErrorClose);
    websocketConnection.setMessageCallback(handleMessage);

    return () => {
      clearRetry();
      websocketConnection.close();
      isPendingRef.current = false;
    };
  }, [active]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (active) {
      setShouldRender(true);
      setLoading(true);
      setError(false);

      timeoutId = setTimeout(() => {
        setAnimationActive(true);
      }, 100);
    } else {
      setAnimationActive(false);

      timeoutId = setTimeout(() => {
        setShouldRender(false);
      }, 350);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [active]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const checkForScrollbar = () => {
      if ($scrollable.current && animationActive) {
        const hasScrollbar =
          $scrollable.current.scrollHeight > $scrollable.current.clientHeight;
        setHasScrollbar(hasScrollbar);
      }
    };

    if (animationActive) timeoutId = setTimeout(checkForScrollbar, 2000);

    window.addEventListener("resize", checkForScrollbar);

    return () => {
      window.removeEventListener("resize", checkForScrollbar);
      clearTimeout(timeoutId);
    };
  }, [$scrollable, animationActive]);

  const handleSuggest = () => {
    const endpointMapping: Record<DataType, string> = {
      [DataType.GOAL]: "suggest_goals",
      [DataType.MEASUREMENT]: "suggest_measurements",
      [DataType.REWARD]: "suggest_rewards",
      [DataType.CONSEQUENCE]: "suggest_consequences",
      [DataType.OBSTACLES]: "suggest_obstacles",
      [DataType.SOLUTIONS]: "suggest_solutions",
      [DataType.ACTION]: "suggest_action_items",
    };

    const getEndpoint = () => {
      const baseData = {
        category: data?.area,
        subcategory: data?.category,
        input_text: data?.prompt
      };

      if (!data?.type)
        return;

      const endpoint = endpointMapping[data?.type];
      const additionalData =
        data?.type === DataType.GOAL
          ? {}
          : data?.type === DataType.ACTION
            ? { goal: data?.goal, domain_id: data?.domain, action_items: data?.actions, regenerate: true }
            : { goal: data?.goal };

      console.log({
        endpoint,
        data: { ...baseData, ...additionalData },
      })

      return {
        endpoint,
        data: { ...baseData, ...additionalData },
      };
    };

    if (!data?.type)
      return;
    
    const endpoint = getEndpoint();
    isPendingRef.current = true;
    websocketConnection.send(JSON.stringify(endpoint));
  };

  const handleOpen = () => {
    // Called on every successful (re)connection. When there is a request
    // awaiting a response, resend it automatically so the user doesn't have to
    // manually refresh. The error label stays visible until data arrives.
    clearRetry();
    retryCountRef.current = 0;

    if (isPendingRef.current) {
      setLoading(true);
      handleSuggest();
    }
  };

  const handleError = () => {
    // Keep the spinner (or the current suggestions) visible and show a message
    // that we're trying to reconnect automatically.
    setError(true);
    setErrorMessage(
      "We're having trouble connecting to the AI assistant. Trying to reconnect automatically..."
    );
    scheduleRetry();
  };

  const handleErrorClose = () => {
    // Stop the utility's own reconnect loop so we stay in control of retries.
    websocketConnection.close();
    setError(true);
    setErrorMessage(
      "We're having trouble connecting to the AI assistant. Trying to reconnect automatically..."
    );
    scheduleRetry();
  };

  const handleRefresh = () => {
    // Reconnect automatically. Only resend the request when the user is still
    // waiting for a first response; otherwise keep the current suggestions.
    // The error label stays visible until data arrives.
    clearRetry();
    if (isPendingRef.current) {
      setLoading(true);
    }
    websocketConnection.close();
    websocketConnection.connect(
      () => {
        if (isPendingRef.current) {
          handleSuggest();
        }
      },
      handleError,
      handleErrorClose
    );
  };

  const handleMessage = (message: string) => {
    const res = JSON.parse(message);

    if (res?.message?.error) {
      console.error("Error received from server:", res.message.error);
      isPendingRef.current = false;
      clearRetry();
      setError(true);
      setErrorMessage(
        "We couldn't get suggestions from the AI assistant right now."
      );
      setLoading(false);
      return;
    }

    if (res.ping || res.pong)
      return;

    const processItems = (items: string[]) => {
      if (data?.type === DataType.ACTION) {
        const parseType = (type: string) => {
          if (type === "action") return "Action Item";
          else if (type === "decision") return "Decision";
          else if (type === "habit") return "Habit";
        };

        return items.map(
          (item: any) =>
            ({
              id: randomID(),
              text: item?.actionItem,
              accountability: item?.accountability,
              type: parseType(item?.type),
            } as Item)
        );
      } else if (data?.type === DataType.MEASUREMENT) {
        return items.map(
          (item: any) =>
            ({
              id: randomID(),
              text: item?.Unit,
              value: item?.Target,
              label: item?.Label,
            } as Item)
        );
      }

      return items.map(
        (item: string) => ({ id: randomID(), text: item } as Item)
      );
    };

    const updateStateWithItems = (items: Item[]) => {
      if (items.length) {
        isPendingRef.current = false;
        clearRetry();
        retryCountRef.current = 0;
        setList(items);
        setLoading(false);
        setError(false);
        setErrorMessage("");

        if (data?.type === DataType.ACTION) {
          setSelected([]);
        } else {
          setSelected(items[0].id);
        }
      }
    };

    if (res?.message && data?.type) {
      const itemsMap: Record<DataType, string[]> = {
        [DataType.GOAL]: res.message.goals,
        [DataType.MEASUREMENT]: res.message.measurements,
        [DataType.REWARD]: res.message.positive_impacts,
        [DataType.CONSEQUENCE]: res.message.consequences,
        [DataType.OBSTACLES]: res.message.obstacles,
        [DataType.SOLUTIONS]: res.message.solutions,
        [DataType.ACTION]: res.message.actionItems,
      };

      const items = itemsMap[data.type];

      if (items) {
        const response: Item[] = processItems(items);
        updateStateWithItems(response);
      }
    }
  };

  const handleClose = () => {
    onSelect?.(null);
  };

  const handleClickOutside = useCallback(
    (event: Event) => {
      if (
        $wrapperRef.current &&
        !$wrapperRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    },
    [handleClose]
  );

  useEffect(() => {
    // The listener is added with the function reference.
    document.addEventListener("mouseup", handleClickOutside as EventListener);

    return () => {
      // The listener is removed with the same function reference.
      document.removeEventListener(
        "mouseup",
        handleClickOutside as EventListener
      );
    };
  }, [handleClickOutside]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const id = event.target.id;

    setSelected((prevSelected) => {
      if (!prevSelected || typeof prevSelected === "string") return [id];
      return prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id];
    });
  };

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelected(event.target.id);
  };

  const handleRegenerate = () => {
    setLoading(true);

    if (error) {
      // Force an immediate reconnect; the pending request will be resent
      // automatically once the socket is open again (see handleOpen). The
      // error label stays visible until data arrives.
      isPendingRef.current = true;
      retryCountRef.current = 0;
      clearRetry();
      websocketConnection.close();
      websocketConnection.connect(handleOpen, handleError, handleErrorClose);
      setList([]);
    }
    else {
      handleSuggest();
    }
  };

  const handleApply = () => {
    if (Array.isArray(selected)) {
      const items = list.filter((item) => selected.includes(item.id));

      onSelect?.(items[0]);
      data?.onSelect?.(items);
    } else {
      const item = list.find((item) => item.id === selected);

      if (item) {
        if (data?.type === DataType.MEASUREMENT) {
          data?.onSelect?.(item);
          onSelect?.(null);
        } else {
          onSelect?.(item);
        }
      }
    }
  };

  const renderList = () => {
    const animateDelayCounter = increaseBy(100);

    return (
      <>
        <Styled.Scrollable ref={$scrollable} $hasScrollbar={hasScrollbar}>
          <Styled.List>
            {list.map((item) => {
              const renderLabel = () => {
                if (data?.type === DataType.ACTION) {
                  return (
                    <Styled.LiContent $type={DataType.ACTION}>
                      <span>
                        <b>Action Item:</b> {item.text}
                      </span>
                      <span>
                        <b>Accountability:</b> {item.accountability}
                      </span>
                      <span>
                        <b>Type:</b> {item.type}
                      </span>
                    </Styled.LiContent>
                  );
                }
                else if (data?.type === DataType.MEASUREMENT) {
                  return (
                    <Styled.LiContent $type={DataType.MEASUREMENT}>
                      <span>{item.label}</span>
                      <span>{item.text}</span>
                      <span>{item.value}</span>
                    </Styled.LiContent>
                  );
                }
                return (
                  <Styled.LiContent>
                    <span>{item.text}</span>
                    <span>{item.value}</span>
                  </Styled.LiContent>
                );
              };

              const renderToggle = () => {
                if (data?.type === DataType.ACTION) {
                  return (
                    <Checkbox
                      id={item.id}
                      checked={
                        selected && Array.isArray(selected)
                          ? selected.includes(item.id)
                          : false
                      }
                      label={renderLabel()}
                      rtl
                      width="100%"
                      onChange={handleChange}
                    />
                  );
                }

                return (
                  <RadioButton
                    id={item.id}
                    checked={selected === item.id}
                    label={renderLabel()}
                    name="assistant"
                    rtl
                    width="100%"
                    onChange={handleRadioChange}
                    styles={{
                      label: {
                        alignItems: 'center'
                      }
                    }}
                  />
                );
              };

              return (
                <Styled.ListItem
                  $active={selected === item.id}
                  $animate={!loading}
                  $animateDelay={animateDelayCounter()}
                  key={item.id}
                >
                  {renderToggle()}
                </Styled.ListItem>
              );
            })}
          </Styled.List>
        </Styled.Scrollable>

        <Styled.Footer
          $animate={!loading}
          $animateDelay={animateDelayCounter()}
        >
          <Button onClick={handleRegenerate} styleType="link-primary">
            <img src={reloadIcon} width={16} height={16} alt="Reload" />
            Regenerate
          </Button>
          <Button
            disabled={
              !selected || (Array.isArray(selected) && !selected.length)
            }
            onClick={handleApply}
          >
            Apply
          </Button>
        </Styled.Footer>
      </>
    );
  };

  if (!shouldRender) return null;

  return ReactDOM.createPortal(
    <Styled.Wrapper
      ref={$wrapperRef}
      $color={data?.color}
      $active={animationActive}
    >
      <Styled.Header>
        <Styled.Title>{data?.title}</Styled.Title>
        <Styled.ToggleClose type="button" onClick={handleClose}>
          <img src={closeIcon} width={24} height={24} alt="Close" />
        </Styled.ToggleClose>
      </Styled.Header>

      <Styled.Details $mb={data?.type === DataType.MEASUREMENT ? 6 : 18}>
        <Styled.Order>{data?.order}</Styled.Order>
        <Styled.DetailsWrap>
          <Styled.Area title={data?.area}>{data?.area}</Styled.Area>
          <Styled.Category title={data?.category}>
            {data?.category}
          </Styled.Category>
        </Styled.DetailsWrap>
      </Styled.Details>

      {data?.type === DataType.MEASUREMENT && !loading && !error && (
        <Styled.Measurement>Label / Unit / Target</Styled.Measurement>
      )}

      <Styled.Content>
        {loading ? (
          <Loader
            title={data?.title || ''}
            loading={loading || error}
            error={errorMessage}
          />
        ) : (
          <>
            {error && errorMessage && (
              <Styled.LoaderError $animate={!!error} $animateDelay={0}>
                {errorMessage}
              </Styled.LoaderError>
            )}
            {renderList()}
          </>
        )}
      </Styled.Content>

      <Styled.Bottom>
        {data?.type === DataType.ACTION && (
          <Styled.Disclaimer>
            NOTE: Select which actions to add to your goal. If more actions are selected than the default items, new actions will be created automatically.
          </Styled.Disclaimer>
        )}

        <img
          src={assistantBeta}
          width={144}
          height={24}
          alt="AI Assistant Beta"
        />
      </Styled.Bottom>
    </Styled.Wrapper>,
    document.getElementById("portal-root") as Element
  );
};

export default Assistant;
